import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Space, Button, message, Tag, Tooltip } from 'antd';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { petAPI, historyAPI } from '../services/api';
import { AppSettings, PetRankingItem, SelectOption, HistoryGranularity } from '../types';
import { compareSnapshotsDesc } from '../utils/snapshot';
import HistoryControls from './HistoryControls';

interface PetRankingsProps {
  settings: AppSettings;
}

const PetRankings: React.FC<PetRankingsProps> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PetRankingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [petId, setPetId] = useState<number | null>(null);
  const [petTypes, setPetTypes] = useState<SelectOption[]>([]);
  // 历史对比相关
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [granularity, setGranularity] = useState<HistoryGranularity>('day');
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [snapshotFrom, setSnapshotFrom] = useState<string | undefined>(undefined);
  const [snapshotTo, setSnapshotTo] = useState<string | undefined>(undefined);

  // 加载宠物类型
  useEffect(() => {
    const loadPetTypes = async () => {
      try {
        const result = await petAPI.getTypes();
        setPetTypes(result);
      } catch (error) {
        console.error('加载宠物类型失败:', error);
      }
    };
    loadPetTypes();
  }, []);

  // 加载数据
  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      if (historyEnabled && snapshotFrom && snapshotTo) {
        // 对比模式
        const res = await historyAPI.compare<PetRankingItem>({
          category: 'pet',
          granularity,
          snapshot_from: snapshotFrom,
          snapshot_to: snapshotTo,
          server_id: settings.serverId ?? undefined,
          pet_id: petId ?? undefined,
          page,
          page_size: settings.pageSize,
        });
        const items = (res.diffs || [])
          .map((d: any, index: number) => {
            const toItem = d.to || {};
            const fromItem = d.from || {};
            const newRank = (d.new_rank ?? toItem.rank ?? 0) as number;
            const oldRank = (d.old_rank ?? fromItem.rank ?? 0) as number;
            const newScore = (d.new_score ?? toItem.pet_score ?? toItem.score ?? 0) as number;
            const oldScore = (d.old_score ?? fromItem.pet_score ?? fromItem.score ?? 0) as number;
            const scoreDelta = newScore - oldScore;
            const rankDelta = newRank - oldRank;
            return {
              ...toItem,
              rank: newRank,
              formatted_score: toItem.formatted_score ?? (toItem.pet_score != null ? String(toItem.pet_score) : (toItem.score != null ? String(toItem.score) : '')),
              filtered_rank: (page - 1) * settings.pageSize + index + 1,
              score_delta: scoreDelta,
              rank_delta: rankDelta,
              is_new: !!d.is_new,
            } as PetRankingItem & { score_delta: number; rank_delta: number; is_new?: boolean };
          });
        setData(items);
        setTotal(res.total ?? items.length);
        setCurrentPage(res.page ?? page);
      } else if (snapshotTo) {
        // 单快照浏览
        const result = await historyAPI.getHistory<PetRankingItem>({
          category: 'pet',
          granularity,
          snapshot: snapshotTo,
          server_id: settings.serverId ?? undefined,
          pet_id: petId ?? undefined,
          page,
          page_size: settings.pageSize,
        });
        let items = (result.items || []).map((item: any) => {
          const rank = (item.rank ?? item.global_rank ?? item.server_rank ?? 0) as number;
          const formatted_score = item.formatted_score ?? (item.pet_score != null ? String(item.pet_score) : (item.score != null ? String(item.score) : ''));
          return { ...item, rank, formatted_score } as PetRankingItem;
        });
        // 历史模式服务端已筛选，无需前端再次过滤
        items.sort((a: any, b: any) => (a.rank && b.rank) ? a.rank - b.rank : (b.pet_score ?? b.score ?? 0) - (a.pet_score ?? a.score ?? 0));
        const itemsWithFilteredRank = items.map((item, index) => ({
          ...item,
          filtered_rank: (page - 1) * settings.pageSize + index + 1,
        }));
        setData(itemsWithFilteredRank);
        setTotal(result.total ?? itemsWithFilteredRank.length);
        setCurrentPage(result.page ?? page);
      } else {
        // 实时榜
        const result = await petAPI.getRankings({
          page,
          page_size: settings.pageSize,
          server_id: settings.serverId,
          pet_id: petId,
          use_units: settings.useUnits,
        });

        const itemsWithFilteredRank = result.items.map((item, index) => ({
          ...item,
          filtered_rank: (page - 1) * settings.pageSize + index + 1,
        }));

        setData(itemsWithFilteredRank);
        setTotal(result.total);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, [settings.pageSize, settings.serverId, settings.useUnits, petId, historyEnabled, granularity, snapshotFrom, snapshotTo]);

  // 拉取快照列表
  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const resp = await historyAPI.listSnapshots({ category: 'pet', granularity, limit: 60 });
  const snaps = (resp.snapshots || []).slice().sort(compareSnapshotsDesc);
        setSnapshots(snaps);
        if (historyEnabled) {
          if (snaps.length >= 2) {
            setSnapshotFrom(snaps[1]);
            setSnapshotTo(snaps[0]);
          }
        } else {
          if (snaps.length >= 1) {
            setSnapshotTo(snaps[0]);
          }
        }
      } catch {}
    };
    fetchSnapshots();
  }, [historyEnabled, granularity, settings.serverId]);

  const handleRefresh = () => {
    loadData(currentPage);
  };

  const handlePetChange = (value: number | null) => {
    setPetId(value);
    setCurrentPage(1);
  };

  const columns = [
    {
      title: '总榜排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <Tag color={rank <= 3 ? 'gold' : rank <= 10 ? 'orange' : 'default'}>
          {rank}
        </Tag>
      ),
    },
    {
      title: '筛选排名',
      dataIndex: 'filtered_rank',
      key: 'filtered_rank',
      width: 80,
      render: (rank: number) => (
        <Tag color="blue">{rank}</Tag>
      ),
    },
    {
      title: '宠物种类',
      dataIndex: 'pet_name',
      key: 'pet_name',
      width: 120,
      render: (name: string) => (
        <span style={{ fontWeight: 'bold', color: '#722ed1' }}>{name}</span>
      ),
    },
    {
      title: '战力',
      dataIndex: 'formatted_score',
      key: 'score',
      width: 100,
      render: (score: string) => (
        <span style={{ fontWeight: 'bold', color: '#f5222d' }}>{score}</span>
      ),
    },
    {
      title: '等级',
      dataIndex: 'owner_level',
      key: 'owner_level',
      width: 70,
      render: (lv: number) => (
        <Tag color="green">{lv}</Tag>
      ),
    },
    {
      title: '宠物昵称',
      dataIndex: 'pet_nickname',
      key: 'pet_nickname',
      width: 120,
      render: (nickname: string) => (
        <Tooltip title={nickname}>
          <span style={{ color: '#fa8c16' }}>{nickname || '无昵称'}</span>
        </Tooltip>
      ),
    },
    {
      title: '主人名字',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 120,
      render: (name: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{name}</span>
      ),
    },
    {
      title: '宠物皮肤',
      dataIndex: 'pet_skin_name',
      key: 'pet_skin_name',
      width: 120,
      render: (skin: string) => skin || '默认',
    },
    {
      title: '主人UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 120,
      render: (uid: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{uid}</span>
      ),
    },
    {
      title: '服务器',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 80,
      render: (serverId: number) => (
        <Tag color="purple">服务器{serverId}</Tag>
      ),
    },
    // 对比模式新增列：分数/排名变化
    ...(historyEnabled ? [
      {
        title: '战力变化',
        dataIndex: 'score_delta',
        key: 'score_delta',
        width: 110,
        align: 'center' as const,
        render: (delta: number) => (
          <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) > 0 ? '#52c41a' : '#f5222d', fontWeight: 500 }}>
            {delta > 0 ? `+${delta}` : `${delta || 0}`}
          </span>
        ),
      },
      {
        title: '排名变化',
        dataIndex: 'rank_delta',
        key: 'rank_delta',
        width: 120,
        align: 'center' as const,
        render: (delta: number, record: any) => {
          if (record?.is_new) {
            return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
          }
          return (
            <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) < 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}>
              {delta < 0 ? `↑ ${Math.abs(delta)}` : delta > 0 ? `↓ ${delta}` : '—'}
            </span>
          );
        },
      },
    ] : []),
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <span>宠物排行榜</span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
              size="small"
            >
              刷新
            </Button>
          </Space>
        }
        extra={
          <Space>
            <HistoryControls
              enabled={historyEnabled}
              granularity={granularity}
              snapshots={snapshots}
              from={snapshotFrom}
              to={snapshotTo}
              onChange={(v) => {
                setHistoryEnabled(v.enabled);
                setGranularity(v.granularity);
                setSnapshotFrom(v.from);
                setSnapshotTo(v.to);
              }}
            />
            <FilterOutlined />
            <Select
              placeholder="选择宠物类型"
              style={{ width: 140 }}
              value={petId}
              onChange={handlePetChange}
              allowClear
            >
              {petTypes.map(pet => (
                <Select.Option key={pet.id} value={pet.id}>
                  {pet.name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        <Table
          className="ranking-table"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(record) => `${record.uid}_${record.pet_name}`}
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            pageSize: settings.pageSize,
            total: total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page) => loadData(page),
          }}
          size="small"
          bordered
          sticky
        />
      </Card>
    </div>
  );
};

export default PetRankings;
